import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { SearchEngineResult, SearchFeedbackRating } from "@shaarp/common";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import { ButtonLoading } from "../button-loading";
import {
  MessageAction,
  MessageActions,
} from "../ai-elements/message";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { TextareaAutosize } from "../ui/textarea-autosize";
import { useToast, useToastError } from "../ui/use-toast";

type FeedbackSearchResultProps = {
  searchId?: string;
  query?: string;
  searchEngineResult?: SearchEngineResult;
  /** When true, renders only the thumb buttons inline without surrounding layout */
  inline?: boolean;
  /** Pre-populated rating from a previously saved search */
  initialRating?: SearchFeedbackRating | null;
  /** Pre-populated comment from a previously saved search */
  initialComment?: string | null;
};

export default function FeedbackSearchResult({
  searchId,
  query,
  searchEngineResult,
  inline = false,
  initialRating = null,
  initialComment = null,
}: FeedbackSearchResultProps) {
  const api = useApi();
  const { t } = useTranslation();

  const RATING_PRESETS: Record<SearchFeedbackRating, string[]> = {
    positive: t("prospecting.feedback.modal.preset.positive", {
      returnObjects: true,
    }) as string[],
    negative: t("prospecting.feedback.modal.preset.negative", {
      returnObjects: true,
    }) as string[],
  };

  const getPlaceholder = () => {
    return selectedRating === "positive"
      ? t("prospecting.feedback.modal.placeholder.positive")
      : t("prospecting.feedback.modal.placeholder.negative");
  };
  const { toast } = useToast();
  const toastError = useToastError();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRating, setSelectedRating] =
    useState<SearchFeedbackRating | null>(initialRating);
  const [submittedRating, setSubmittedRating] =
    useState<SearchFeedbackRating | null>(initialRating);
  const [feedback, setFeedback] = useState(initialComment ?? "");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!selectedRating) {
      toastError();
      return;
    }

    setIsLoading(true);
    try {
      await api.post("/search-engine/search/feedback", {
        searchId,
        rating: selectedRating,
        feedback: feedback.trim(),
        query,
        engine: searchEngineResult,
      });

      toast({
        title: t("prospecting.feedback.toast.success.title"),
        description: t("prospecting.feedback.toast.success.description"),
      });

      setSubmittedRating(selectedRating);
      setIsDialogOpen(false);
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const handleRatingClick = (rating: SearchFeedbackRating) => {
    setSelectedRating(rating);
    setIsDialogOpen(true);
  };

  const thumbButtons = (
    <MessageActions>
      <MessageAction
        variant="outline"
        tooltip={t("prospecting.feedback.thumbsUp")}
        label={t("prospecting.feedback.thumbsUp")}
        onClick={() => handleRatingClick("positive")}
      >
        <ThumbsUp
          className={`size-3 ${submittedRating === "positive" ? "fill-current" : ""}`}
        />
      </MessageAction>
      <MessageAction
        variant="outline"
        tooltip={t("prospecting.feedback.thumbsDown")}
        label={t("prospecting.feedback.thumbsDown")}
        onClick={() => handleRatingClick("negative")}
      >
        <ThumbsDown
          className={`size-3 ${submittedRating === "negative" ? "fill-current" : ""}`}
        />
      </MessageAction>
    </MessageActions>
  );

  return (
    <>
      {inline ? (
        thumbButtons
      ) : (
        <div className="flex justify-center w-full">
          <div className="w-full max-w-4xl">
            <div className="flex items-center justify-center gap-4">
              <p className="text-xs text-gray-500 dark:text-gray-500">
                {t("prospecting.feedback.question")}
              </p>
              {thumbButtons}
            </div>
          </div>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[600px] w-full">
          <DialogHeader>
            <DialogTitle>{t("prospecting.feedback.modal.title")}</DialogTitle>
            <DialogDescription>
              {t("prospecting.feedback.modal.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <div className="flex justify-center gap-2">
              <Button
                variant={selectedRating === "positive" ? "default" : "outline"}
                size="lg"
                onClick={() => setSelectedRating("positive")}
                className="gap-2"
              >
                <ThumbsUp className="w-5 h-5" />
              </Button>
              <Button
                variant={selectedRating === "negative" ? "default" : "outline"}
                size="lg"
                onClick={() => setSelectedRating("negative")}
                className="gap-2"
              >
                <ThumbsDown className="w-5 h-5" />
              </Button>
            </div>

            {selectedRating && (
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {t("prospecting.feedback.modal.quickFeedback")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {RATING_PRESETS[selectedRating].map((preset) => (
                    <Button
                      key={preset}
                      variant="outline"
                      size="sm"
                      onClick={() => setFeedback(preset)}
                      className="h-8 text-xs"
                    >
                      {preset}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("prospecting.feedback.modal.suggestions")}
              </label>
              <TextareaAutosize
                placeholder={getPlaceholder()}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                maxLength={500}
                className="min-h-[120px]"
              />
              <p className="text-xs text-right text-gray-500 dark:text-gray-400">
                {feedback.length}/500
              </p>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">
                {t("prospecting.feedback.modal.cancel")}
              </Button>
            </DialogClose>
            <ButtonLoading
              onClick={handleSubmit}
              disabled={!selectedRating}
              isLoading={isLoading}
            >
              {t("prospecting.feedback.modal.submit")}
            </ButtonLoading>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
